<template>
  <div class="phone-wrapper">
    <label
      for="phoneNumber"
      class="phone-number-label"
      :class="{ 'tn-error': error }"
    >
      {{ label }}
    </label>
    <div
      class="phone-number"
      :class="{ 'tn-disabled': disabled, 'tn-error': error }"
    >
      <div class="phone-dropdown">
        <VSelect
          v-model="mobilePhoneNumber.country"
          class="mb-3 countryField"
          :disabled="disabled || disablePrefix"
          :hide-details="hideDetails"
          :items="countryList"
          :menu-props="menuProps"
          :country="country"
          flat
          :class="{ 'is-focused': focused }"
          @change="changedMobileCountry"
        >
          <template v-slot:append>
            <NsIcon
              class="tn-icon"
              icon-name="icon-chevron-down"
              height="20"
              width="20"
            />
          </template>
          <template slot="selection">
            <span class="selected-code">
              {{ mobilePhoneNumber.country }} ({{ phonePrefix }})
            </span>
          </template>
        </VSelect>
      </div>
      <div class="phone-input">
        <VTextField
          id="phoneNumber"
          v-model="mobilePhoneNumber.number"
          name="phoneNumber"
          :maxlength="maxLength"
          class="phoneField"
          type="tel"
          :disabled="disabled"
          :hide-details="hideDetails"
          :mask="phoneMask"
          :rules="rules"
          :placeholder="placeholder"
          validate-on-blur
          @keyup="onlyNumbers"
          @blur="receiveBlur"
          @input="receiveInput"
          @focus="focused = true"
        >
        </VTextField>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Ben Layton
 * Original Team: Fore oh Four!
 */
import { VTextField, VSelect } from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';
import phoneFormats from './phoneFormats.js';

export default {
  name: 'NsPhoneNumber',
  components: {
    NsIcon,
    VTextField,
    VSelect,
  },
  props: {
    /**
     * Countries as a key:value object
     *  -- example: AR:"Argentina"
     * {
     *   AR: "Argentina",
     *   AT: "Austria"
     * }
     */
    countryNames: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Field Label
     */
    label: {
      type: String,
      default: 'Mobile Phone',
    },
    /**
     * Required message
     */
    errorMessage: {
      type: String,
      default: 'This field is required',
    },
    /**
     * Invalid Message
     */
    invalidMessage: {
      type: String,
      default: 'Invalid Phone Number Format',
    },
    /**
     * Hide details
     */
    hideDetails: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable the country prefix
     */
    disablePrefix: {
      type: Boolean,
      default: false,
    },
    /**
     * Default country code
     */
    country: {
      type: String,
      default: 'US',
    },
    number: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      error: false,
      mobilePhoneNumber: { country: this.country, number: this.number },
      phonePrefix: phoneFormats.US.countryPhoneCode,
      minLength: phoneFormats.US.minLength,
      maxLength: phoneFormats.US.minLength,
      phoneMask: phoneFormats.US.mask,
      form: {},
      rules: [
        (v) => (v || '').length > 0 || this.errorMessage,
        (v) => (v || '').length >= this.minLength || this.invalidMessage,
        (v) => !v || /^[0-9 ()+-]+$/.test(v.trim()) || this.invalidMessage,
        (v) =>
          (v || '').length <= (this.maxLength ? this.maxLength : 100) ||
          this.invalidMessage,
      ],
      placeholder: phoneFormats.US.placeholder,
      phoneValid: null,
      menuProps: { minWidth: 350, offsetY: true, flat: true },
      focused: false,
    };
  },
  computed: {
    countryList() {
      let countryList;
      // if countryNames is given as a prop
      let countryCodes = Object.keys(this.countryNames);
      if (countryCodes.length > 0) {
        countryList = countryCodes.reduce((acc, code) => {
          if (phoneFormats[code]) {
            acc.push(
              this.createCountryItem(
                this.countryNames[code],
                phoneFormats[code].countryPhoneCode,
                code
              )
            );
          }

          return acc;
        }, []);
      } else {
        // use default phoneFormats
        countryCodes = Object.keys(phoneFormats);
        countryList = countryCodes.map((code) =>
          this.createCountryItem(
            phoneFormats[code].countryName,
            phoneFormats[code].countryPhoneCode,
            code
          )
        );
      }

      countryList.sort((v1, v2) => v1.text.localeCompare(v2.text));

      return countryList;
    },
    isNumber() {
      let type = '';
      if (this.country === 'JP') {
        type = 'number';
      }
      return type;
    },
  },
  watch: {
    country() {
      this.changedMobileCountry(this.country);
    },
    number(newNumber) {
      if (newNumber !== this.mobilePhoneNumber.number) {
        this.mobilePhoneNumber.number = newNumber;
        this.receiveInput();
      }
    },
  },
  created() {
    this.changedMobileCountry(this.country);
  },
  methods: {
    createCountryItem(countryName, prefix, value) {
      return {
        text: `${countryName} (${prefix})`,
        value,
      };
    },
    onlyNumbers() {
      this.mobilePhoneNumber.number = this.mobilePhoneNumber.number.replace(
        /[^\d]/,
        ''
      );
    },
    closeDialog() {
      this.$emit('closeDialog');
      this.dialogError = null;
    },
    changedMobileCountry(value) {
      let countryFormat = phoneFormats[value];
      this.phonePrefix = countryFormat.countryPhoneCode;
      this.phoneMask = countryFormat.mask;
      this.placeholder = countryFormat.placeholder;
      this.minLength = countryFormat.minLength;
      this.maxLength = countryFormat.maxLength;
      this.mobilePhoneNumber.country = value;
    },
    receiveBlur() {
      this.focused = false;
      this.receiveError();
      this.$emit('input', this.mobilePhoneNumber);
    },
    receiveInput() {
      this.receiveError();
      this.$emit('input', this.mobilePhoneNumber);
    },
    receiveError() {
      const valid = this.rules.every(
        (fn) => fn(this.mobilePhoneNumber.number) === true
      );
      this.$emit('update:error', (this.error = !valid));
    },
    updatePhone() {
      const self = this;
      const updatedMobilePhoneNumber = {
        phoneNumbers: {
          mobile: {
            country: self.mobilePhoneNumber.country,
            value: self.mobilePhoneNumber.number,
          },
        },
      };
      this.$emit('phoneNumberUpdated', updatedMobilePhoneNumber);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/gridStyles";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

$color-dryink: map.get(colors.$baseColors, dryink);
$color-error: map.get(colors.$baseColors, 'color-error');
$color-white: map.get(colors.$baseColors, white);

label.phone-number-label {
  &:hover {
    cursor: text;
  }
  &.tn-error {
    color: $color-error !important;
  }
}
.phone-number {
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  flex-flow: row nowrap;

  .phone-dropdown input {
    padding: 100px;
    border: 1px solid map.get(colors.$baseColors, dryink);
  }

  .countryField input {
    border-right: none !important;
  }

  .phoneField input {
    border-right: 1px solid map.get(colors.$baseColors, dryink);
    border-top: 1px solid map.get(colors.$baseColors, dryink);
    border-bottom: 1px solid map.get(colors.$baseColors, dryink);
    &::placeholder {
      font-family: map.get(
        typographyVariables.$typography,
        font-family-primary
      );
      letter-spacing: 5px;
    }
  }

  .selected-code {
    position: absolute;
    z-index: 2;
    left: 10px;
  }
  .phone-dropdown {
    max-width: 130px;
  }
  .phone-input {
    width: calc(100% - 130px);
    padding: 0;
  }
}

::v-deep {
  .v-input__control input {
    background-color: $color-white;
    border: 1px solid $color-dryink;
  }

  .v-text-field > .v-input__control > .v-input__slot > .v-text-field__slot {
    display: block !important;
  }

  .v-select__slot .v-input__append-inner {
    @media screen and (max-width: 767px) {
      bottom: 13px;
    }
  }
  .v-list__tile--active.primary--text,
  .v-list__tile:hover {
    color: map.get(colors.$baseColors, blackhole) !important;
    font-weight: bold;
    border-left: 2px solid map.get(colors.$baseColors, blackhole) !important;
  }
  .v-list__tile {
    border-bottom: 1px solid map.get(colors.$baseColors, londonfog) !important;
    &:last-child {
      border-bottom: none;
    }
  }
  .v-select {
    .v-input__append-inner {
      transform-style: preserve-3d;
      transform: rotateX(0deg);
      transition: transform 0.6s ease-in-out;
    }
  }
  .v-select--is-menu-active {
    .v-input__append-inner {
      transform: rotateX(180deg);
    }
  }
  .v-list {
    padding: 0;
  }
  .v-text-field__slot {
    flex-direction: unset;
    display: block !important;
  }

  .v-text-field__slot input {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
  }
  .v-select__selections input {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
}

.tn-disabled {
  ::v-deep {
    .tn-icon {
      color: map.get(colors.$baseColors, gandalf) !important;
    }

    .v-input--is-disabled {
      .v-input__control input {
        border-color: map.get(colors.$baseColors, gandalf) !important;
        border-radius: 0;
      }
    }
  }
}

.tn-error {
  ::v-deep {
    .v-input input {
      border-color: $color-error !important;
    }
  }
}
::v-deep {
  .phoneField {
    input[type='tel']:focus {
      border: 1px solid map.get(colors.$theme, $key: 'accent-color-b') !important;
    }
  }
  .countryField.is-focused input[type='text'] {
    border: 1px solid map.get(colors.$theme, 'accent-color-b') !important;
  }
}
</style>

<style lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";

.v-menu__content {
  &.menuable__content__active {
    box-shadow: none;
    border: 1px solid map.get(colors.$baseColors, dryink) !important;
    border-top: none !important;
  }
}
</style>
